import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";
import { setChallengeHandler } from "@/lib/antibot";

/*
 * Modale du défi anti bot adaptatif.
 *
 * Elle n'apparaît JAMAIS en usage normal : uniquement quand le backend
 * détecte un comportement anormal (limite de débit dépassée) et renvoie un
 * défi signé dans sa réponse 429. Une fois la question résolue, la requête
 * d'origine est rejouée automatiquement par l'interceptor de lib/api.js.
 *
 * Aucun service tiers, aucune donnée personnelle : conforme Conservation
 * Nulle. Texte sans aucun tiret (règle éditoriale propriétaire).
 */
/* ----- Formulaire du defi : question, saisie et validation ----- */

const ChallengeForm = ({ challenge, answer, error, loading, onAnswerChange, onSubmit }) => (
  <form onSubmit={onSubmit} className="mt-2 space-y-4">
    <div className="space-y-2">
      <Label
        htmlFor="antibot-answer"
        data-testid="antibot-challenge-question"
        className="text-base font-medium text-foreground"
      >
        {challenge ? challenge.question : ""}
      </Label>
      <Input
        id="antibot-answer"
        data-testid="antibot-challenge-answer-input"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={answer}
        onChange={onAnswerChange}
        placeholder="Ta réponse"
        autoFocus
        className="h-11 text-base"
        aria-invalid={Boolean(error)}
      />
      {error ? (
        <p
          data-testid="antibot-challenge-error"
          role="alert"
          className="text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}
    </div>
    <Button
      type="submit"
      data-testid="antibot-challenge-submit-button"
      disabled={loading}
      className="h-11 w-full text-base font-semibold"
    >
      {loading ? "Vérification en cours" : "Valider et continuer"}
    </Button>
  </form>
);

export const AntiBotChallenge = () => {
  const [open, setOpen] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const pendingRef = useRef(null);

  useEffect(() => {
    const unregister = setChallengeHandler(
      (challengeData) =>
        new Promise((resolve, reject) => {
          if (pendingRef.current) {
            // Un défi est déjà affiché : les autres requêtes bloquées
            // attendent la même résolution.
            pendingRef.current.subscribers.push({ resolve, reject });
            return;
          }
          pendingRef.current = { subscribers: [{ resolve, reject }] };
          setChallenge(challengeData);
          setAnswer("");
          setError("");
          setOpen(true);
        }),
    );
    return unregister;
  }, []);

  const finish = (success) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    setChallenge(null);
    setAnswer("");
    setError("");
    if (pending) {
      pending.subscribers.forEach(({ resolve, reject }) =>
        success ? resolve() : reject(new Error("defi_abandonne")),
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!challenge || loading) return;
    const trimmed = answer.trim();
    if (!trimmed) {
      setError("Entre ta r\u00e9ponse pour continuer.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/challenge/verify`,
        { token: challenge.token, answer: trimmed },
        { _skipChallenge: true },
      );
      finish(true);
    } catch (err) {
      const data = (err.response && err.response.data) || {};
      if (data.challenge) {
        // Jeton expiré ou tentatives épuisées : nouvelle question fournie.
        setChallenge(data.challenge);
        setAnswer("");
      }
      setError(data.detail || "V\u00e9rification impossible. R\u00e9essaie dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    if (!next && !loading) {
      finish(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="antibot-challenge-modal"
        className="max-w-sm rounded-2xl border border-border bg-card p-6 shadow-lg"
      >
        <DialogHeader className="space-y-3 text-left">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary"
            aria-hidden="true"
          >
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-lg font-semibold text-foreground">
            Vérification rapide
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
            Nous avons détecté une activité inhabituelle depuis ta connexion.
            Réponds à cette petite question pour reprendre exactement où tu en
            étais. Rien n'est perdu.
          </DialogDescription>
        </DialogHeader>

        <ChallengeForm
          challenge={challenge}
          answer={answer}
          error={error}
          loading={loading}
          onAnswerChange={(event) => setAnswer(event.target.value)}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
};
